const ONLINE_PAYMENT_METHODS = new Set(['pix', 'card', 'mercadopago']);

export function normalizePaymentMethod(paymentMethod) {
  return String(paymentMethod || '').trim().toLowerCase();
}

export function isOnlinePaymentMethod(paymentMethod) {
  return ONLINE_PAYMENT_METHODS.has(normalizePaymentMethod(paymentMethod));
}

/**
 * Dinheiro / retirada: imprime na criacao do pedido.
 * Pix / cartao: imprime somente apos pagamento confirmado (status != payment_pending).
 */
export function shouldAutoPrintOrder(pedido) {
  if (!pedido?.id) return false;

  const status = String(pedido.status || '').trim().toLowerCase();
  if (['cancelado', 'cancelled'].includes(status)) return false;
  if (status === 'payment_pending') return false;

  const method = normalizePaymentMethod(pedido.payment_method);
  if (isOnlinePaymentMethod(method) && status === 'payment_pending') {
    return false;
  }

  return true;
}

/**
 * Marca pedidos ja existentes para nunca serem impressos ao abrir a tela.
 */
export function bootstrapOrderPrintTracking(pedidos, { knownIds, printedIds, statusById }) {
  (pedidos || []).forEach((pedido) => {
    if (!pedido?.id) return;
    const id = Number(pedido.id);
    if (!Number.isInteger(id)) return;
    knownIds.add(id);
    printedIds.add(id);
    statusById.set(id, String(pedido.status || '').trim().toLowerCase());
  });
}

/**
 * Decide se um pedido deve imprimir no polling:
 * - pedido novo e elegivel
 * - ou pix/cartao que acabou de ser pago (payment_pending -> pendente)
 */
export function shouldPrintOnOrderPoll(pedido, { knownIds, statusById }) {
  if (!pedido?.id) return false;

  const id = Number(pedido.id);
  if (!Number.isInteger(id)) return false;
  const status = String(pedido.status || '').trim().toLowerCase();
  const isNovo = !knownIds.has(id);

  if (isNovo) {
    knownIds.add(id);
    statusById.set(id, status);
    return shouldAutoPrintOrder(pedido);
  }

  const previousStatus = statusById.get(id);
  statusById.set(id, status);

  if (previousStatus === 'payment_pending' && status === 'pendente' && shouldAutoPrintOrder(pedido)) {
    return true;
  }

  return false;
}

const printedStorageKey = (lanchoneteId) => `@AllDelivery:printed-orders:${lanchoneteId}`;

export function loadPersistedPrintedIds(lanchoneteId) {
  if (!lanchoneteId || typeof window === 'undefined') return [];
  try {
    const value = JSON.parse(window.localStorage.getItem(printedStorageKey(lanchoneteId)) || '[]');
    return Array.isArray(value) ? value.map(Number).filter(Number.isInteger) : [];
  } catch (_) {
    return [];
  }
}

export function persistPrintedId(lanchoneteId, orderId) {
  if (!lanchoneteId || !orderId || typeof window === 'undefined') return;
  const ids = loadPersistedPrintedIds(lanchoneteId).filter((id) => id !== Number(orderId));
  ids.push(Number(orderId));
  // Somente os ultimos pedidos sao necessarios para impedir segunda via.
  window.localStorage.setItem(printedStorageKey(lanchoneteId), JSON.stringify(ids.slice(-300)));
}

export function wasPrintedPersisted(lanchoneteId, orderId) {
  const id = Number(orderId);
  return Number.isInteger(id) && loadPersistedPrintedIds(lanchoneteId).includes(id);
}

export function removePersistedPrintedId(lanchoneteId, orderId) {
  if (!lanchoneteId || !orderId || typeof window === 'undefined') return;
  const id = Number(orderId);
  const ids = loadPersistedPrintedIds(lanchoneteId).filter((item) => item !== id);
  window.localStorage.setItem(printedStorageKey(lanchoneteId), JSON.stringify(ids));
}

export function recentEligibleOrders(pedidos, printedIds, maxAgeMs = 5 * 60 * 1000) {
  const now = Date.now();
  return (pedidos || []).filter((pedido) => {
    if (!pedido?.id || printedIds.has(Number(pedido.id))) return false;
    const createdAt = new Date(pedido.created_at).getTime();
    return Number.isFinite(createdAt) && now - createdAt >= 0 && now - createdAt <= maxAgeMs
      && shouldAutoPrintOrder(pedido);
  });
}

// Compatibilidade com chamadas antigas. A recuperacao agora inclui qualquer
// pedido elegivel, nao apenas pedidos identificados como "Mesa Livre".
export const recentFreeTableOrders = recentEligibleOrders;
